import * as Sentry from "@sentry/react";
import { DefaultError, Mutation, Query } from "@tanstack/react-query";
import { AxiosError, isAxiosError } from "axios";

class UnexpectedApiErrorMessage extends Error {
  constructor(message: string) {
    super(message);
    this.name = "UnexpectedApiErrorMessage";
  }
}

export const reportUnexpectedApiErrorMessage = (error: AxiosError) => {
  Sentry.withScope((scope) => {
    scope.setLevel("error");
    scope.setContext("response", {
      data: error.response?.data,
      status: error.response?.status,
      headers: error.response?.headers,
    });
    Sentry.captureException(new UnexpectedApiErrorMessage(error.message));
    console.error(error.response?.data);
  });
};

const getExceptionLevel = (error: DefaultError): Sentry.SeverityLevel => {
  if (
    isAxiosError(error) &&
    error.response?.status &&
    ![403, 500].includes(error.response.status)
  ) {
    return "info";
  }
  return "error";
};

export const captureAxiosException = (error: AxiosError) => {
  if (!error || error.response?.status === 401) return;
  Sentry.withScope((scope) => {
    scope.setLevel(getExceptionLevel(error));
    scope.setContext("request", {
      data: error.config?.data,
      response: error.response?.data,
      status: error.response?.status,
      headers: error.response?.headers,
    });
    Sentry.captureException(error);
  });
};

export const captureMutationException = (
  error: DefaultError,
  mutation: Mutation<unknown, unknown, unknown, unknown>
) => {
  Sentry.withScope((scope) => {
    scope.setLevel(getExceptionLevel(error));
    scope.setContext("request", {
      data: error.config?.data,
      response: error.response?.data,
      status: error.response?.status,
      headers: error.response?.headers,
    });
    scope.setContext("mutation", {
      mutationId: mutation.mutationId,
      variables: mutation.state.variables,
    });
    Sentry.captureException(error);
  });
};

export const captureQueryException = (
  error: DefaultError,
  query: Query<unknown, unknown, unknown, readonly unknown[]>
) => {
  Sentry.withScope((scope) => {
    scope.setLevel(getExceptionLevel(error));
    scope.setContext("query", { queryHash: query.queryHash });
    const fingerprint = query.queryKey.map((key) => {
      if (typeof key === "number") return ":id";
      if (key === null) return ":null";
      if (typeof key === "object") return ":params";
      return String(key);
    });
    scope.setFingerprint(fingerprint);
    Sentry.captureException(error);
  });
};
